import {
  deburr,
  groupBy,
  isEmpty,
  isNil,
  last,
  maxBy,
  minBy,
  sortBy,
  sum,
} from "lodash";
import {
  itemSize,
  portionOf,
  textItems,
  unionRect,
  viewportRect,
} from "./pdf-geometry";

const SAME_LINE = 0.5;
const COLUMN_GAP = 3;
const WORD_GAP = 0.15;
const BIG = 1.15;
const NEAR_BODY = 0.95;
const CAPITALS = 0.85;
const LETTER_SHARE = 0.6;
const RUNNING_DRIFT = 0.05;
const MIN_LENGTH = 3;
const MAX_LENGTH = 90;
const MIN_LETTERS = 3;
const MAX_WORDS = 14;
const CAPITAL_LETTERS = 4;
const CAPITAL_WORDS = 8;
const NUMBERED_WORDS = 10;
const TITLE_WORDS = 2;
const FRAME_HEIGHTS = 3;
const FRAME_SHARE = 0.8;
const FRAME_SLACK = 1;

const BOLD_FONT = /bold|black|heavy|semibold|demibold|extrabold/i;
const LEADERS = /[.…_·•-](?:\s*[.…_·•-]){2,}/g;
const FILLER = /[\s.…_·•-]/g;
const MARKER = /\{\{|\}\}|\[[A-Z][A-Za-z0-9]*\]/;
const LINKS = /@|www\.|http|€/i;
const SENTENCE_END = /[.,;]$/;
const COLON_END = /\s*:$/;
const NUMBERED = /^(\d{1,2}|[IVX]{1,4}|[A-H])[.)]\s+\S/;
const SPACE_END = /\s$/;
const SPACE_START = /^\s/;

const isLetter = (character) =>
  character.toLowerCase() !== character.toUpperCase();

const isUpper = (character) =>
  isLetter(character) && character === character.toUpperCase();

const collapse = (text) => text.replace(/\s+/g, " ").trim();

const wordCount = (text) => text.split(" ").filter(Boolean).length;

const isBoldFont = (font) =>
  Boolean(font?.bold || font?.black || BOLD_FONT.test(font?.name ?? ""));

const placed = (item, fonts) => ({
  item,
  x: item.transform[4],
  baseline: item.transform[5],
  width: item.width || 0,
  size: itemSize(item),
  chars: item.str.length,
  bold: isBoldFont(fonts.get(item.fontName)),
});

const groupLines = (entries) => {
  const lines = [];
  for (const entry of sortBy(entries, [(each) => -each.baseline, "x"])) {
    const line = last(lines);
    const first = line?.[0];
    if (
      !isNil(first) &&
      Math.abs(first.baseline - entry.baseline) <
        SAME_LINE * Math.min(first.size, entry.size)
    )
      line.push(entry);
    else lines.push([entry]);
  }
  return lines;
};

const gapBetween = (previous, entry) => entry.x - (previous.x + previous.width);

const splitFragments = (line) => {
  const fragments = [];
  for (const entry of sortBy(line, "x")) {
    const fragment = last(fragments);
    const previous = isNil(fragment) ? null : last(fragment);
    if (
      isNil(previous) ||
      gapBetween(previous, entry) >
        COLUMN_GAP * Math.min(previous.size, entry.size)
    )
      fragments.push([entry]);
    else fragment.push(entry);
  }
  return fragments;
};

const joinText = (entries) =>
  entries.reduce((text, entry, index) => {
    if (index === 0) return entry.item.str;
    const previous = entries[index - 1];
    const spaced =
      gapBetween(previous, entry) > WORD_GAP * entry.size &&
      !SPACE_END.test(text) &&
      !SPACE_START.test(entry.item.str);
    return `${text}${spaced ? " " : ""}${entry.item.str}`;
  }, "");

const meaningful = (entry) => entry.item.str.replace(FILLER, "").length;

const toFragment = ({ entries, viewport, row }) => {
  const weighed = sum(entries.map(meaningful)) > 0;
  const weight = weighed ? meaningful : (entry) => entry.chars;
  const total = sum(entries.map(weight));
  const boldTotal = sum(entries.filter((entry) => entry.bold).map(weight));
  return {
    text: joinText(entries),
    size: maxBy(entries, weight).size,
    bold: boldTotal * 2 > total,
    row,
    rect: viewportRect(
      viewport,
      entries
        .map((entry) => portionOf(entry.item, 0, entry.item.str.length))
        .reduce(unionRect),
    ),
    spans: entries.map((entry) => [entry.size, entry.chars]),
  };
};

const visibleItems = (items) =>
  textItems(items).filter((item) => !isEmpty(item.str.trim()));

export const pageLines = ({ items, viewport, fonts = new Map() }) =>
  groupLines(visibleItems(items).map((item) => placed(item, fonts))).flatMap(
    (line, row) =>
      splitFragments(line).map((entries) =>
        toFragment({ entries, viewport, row }),
      ),
  );

const weightedMedian = (spans) => {
  const sorted = sortBy(
    spans.filter(([, weight]) => weight > 0),
    ([size]) => size,
  );
  const half = sum(sorted.map(([, weight]) => weight)) / 2;
  let seen = 0;
  for (const [size, weight] of sorted) {
    seen += weight;
    if (seen >= half) return size;
  }
  return 0;
};

const cleaned = (text) => {
  const plain = collapse(text.replace(LEADERS, " "));
  const colon = COLON_END.test(plain);
  return { plain, colon, text: colon ? plain.replace(COLON_END, "") : plain };
};

const letterStats = (text) => {
  const characters = [...text].filter((character) => character.trim());
  const letters = characters.filter(isLetter);
  return {
    letters: letters.length,
    share: characters.length > 0 ? letters.length / characters.length : 0,
    upper:
      letters.length > 0 ? letters.filter(isUpper).length / letters.length : 0,
  };
};

const runningKey = (text) => collapse(deburr(text).toLowerCase());

const runningFragments = ({ pages, candidates }) => {
  if (pages.length < 2) return new Set();
  const running = new Set();
  for (const same of Object.values(groupBy(candidates, "key"))) {
    for (const candidate of same) {
      const height = pages[candidate.line.page]?.height ?? 0;
      const repeated = same.some(
        (other) =>
          other.line.page !== candidate.line.page &&
          Math.abs(other.line.rect.top - candidate.line.rect.top) <=
            RUNNING_DRIFT * height,
      );
      if (repeated) running.add(candidate.line);
    }
  }
  return running;
};

const isRejected = ({ line, text, plain, stats }) =>
  MARKER.test(line.text) ||
  text.length < MIN_LENGTH ||
  text.length > MAX_LENGTH ||
  stats.letters < MIN_LETTERS ||
  wordCount(text) > MAX_WORDS ||
  stats.share < LETTER_SHARE ||
  LINKS.test(text) ||
  SENTENCE_END.test(plain);

const isStrong = ({ line, text, ratio, stats }) =>
  ratio >= BIG ||
  (line.bold && ratio >= NEAR_BODY) ||
  (stats.letters >= CAPITAL_LETTERS &&
    stats.upper >= CAPITALS &&
    ratio >= NEAR_BODY &&
    wordCount(text) <= CAPITAL_WORDS) ||
  (NUMBERED.test(text) && wordCount(text) <= NUMBERED_WORDS);

const keepsColon = ({ line, text, ratio, stats, alone }) =>
  ratio >= BIG ||
  ((line.bold || stats.upper >= CAPITALS) &&
    wordCount(text) >= TITLE_WORDS &&
    alone);

const isPlainHeading = (candidate) =>
  !candidate.colon && !isRejected(candidate) && isStrong(candidate);

const withNeighbours = (candidates) => {
  const rows = groupBy(candidates, ({ line }) => `${line.page}:${line.row}`);
  return candidates.map((candidate) => ({
    ...candidate,
    alone: rows[`${candidate.line.page}:${candidate.line.row}`].every(
      (other) => other === candidate || isPlainHeading(other),
    ),
  }));
};

const sentenceCase = (text) => {
  const lower = text.toLowerCase();
  const index = lower.split("").findIndex(isLetter);
  if (index < 0) return lower;
  return `${lower.slice(0, index)}${lower[index].toUpperCase()}${lower.slice(index + 1)}`;
};

const area = (rect) => rect.width * rect.height;

const contains = (outer, inner) =>
  inner.left >= outer.left - FRAME_SLACK &&
  inner.top >= outer.top - FRAME_SLACK &&
  inner.left + inner.width <= outer.left + outer.width + FRAME_SLACK &&
  inner.top + inner.height <= outer.top + outer.height + FRAME_SLACK;

const frameOf = ({ rect, frames, page }) => {
  const pageArea = (page?.width ?? 0) * (page?.height ?? 0);
  const frame = minBy(
    (frames || []).filter(
      (candidate) =>
        candidate.height >= FRAME_HEIGHTS * rect.height &&
        (!(pageArea > 0) || area(candidate) < FRAME_SHARE * pageArea) &&
        contains(candidate, rect),
    ),
    area,
  );
  if (isNil(frame)) return null;
  return {
    left: frame.left,
    top: frame.top,
    right: frame.left + frame.width,
    bottom: frame.top + frame.height,
  };
};

const toHeading = ({ line, text, stats }, { pages, frames }) => ({
  page: line.page,
  text: stats.upper >= CAPITALS ? sentenceCase(text) : text,
  top: line.rect.top,
  bottom: line.rect.top + line.rect.height,
  left: line.rect.left,
  right: line.rect.left + line.rect.width,
  height: line.rect.height,
  frame: frameOf({
    rect: line.rect,
    frames: frames[line.page],
    page: pages[line.page],
  }),
});

export const pickHeadings = ({ pages, lines, frames = [] }) => {
  const bodySize = weightedMedian(lines.flatMap((line) => line.spans));
  if (!(bodySize > 0)) return [];
  const candidates = withNeighbours(
    lines.map((line) => {
      const { plain, colon, text } = cleaned(line.text);
      return {
        line,
        plain,
        colon,
        text,
        key: runningKey(text),
        ratio: line.size / bodySize,
        stats: letterStats(text),
      };
    }),
  );
  const running = runningFragments({ pages, candidates });
  return candidates
    .filter(
      (candidate) =>
        !isRejected(candidate) &&
        !running.has(candidate.line) &&
        (!candidate.colon || keepsColon(candidate)) &&
        isStrong(candidate),
    )
    .map((candidate) => toHeading(candidate, { pages, frames }));
};
